import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, ArrowLeft, ShieldCheck, Loader2 } from "lucide-react";

export default function MemberResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Reset link is missing a token"); return; }
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/hub/reset-password`,
        { token, password: pw },
      );
      toast.success("Password updated — please sign in with your new password");
      setTimeout(() => navigate("/member/login", { replace: true }), 900);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reset password");
    } finally { setBusy(false); }
  };

  return (
    <section className="min-h-[calc(100vh-80px)] py-16 px-5" data-testid="member-reset-page">
      <div className="max-w-md mx-auto">
        <Link to="/member/login" className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-white/50 hover:text-white mb-6">
          <ArrowLeft size={12} /> Back to sign-in
        </Link>
        <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Reset password</div>
        <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none mb-3 flex items-center gap-3">
          <ShieldCheck className="brand-blue" size={30} /> New Password.
        </h1>
        <p className="text-sm text-white/60 mb-8">Choose a strong password. Minimum 6 characters.</p>

        <form onSubmit={submit} className="border border-white/12 bg-[#131A2B] p-6 space-y-4">
          <PwField label="New password" value={pw} onChange={setPw} testId="reset-new" autoFocus />
          <PwField label="Confirm password" value={pw2} onChange={setPw2} testId="reset-confirm" />
          <button type="submit" disabled={busy || !token}
            data-testid="reset-submit"
            className="w-full bg-brand-orange text-black py-3 font-bold text-sm uppercase tracking-widest hover:bg-white disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
            {busy ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>
    </section>
  );
}

function PwField({ label, value, onChange, testId, autoFocus }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1.5">{label}</div>
      <div className="flex items-center border border-white/20 focus-within:border-brand-orange bg-[#0B1120]">
        <Lock size={14} className="ml-3 text-white/40" />
        <input type="password" value={value} onChange={(e) => onChange(e.target.value)}
          data-testid={testId} autoFocus={autoFocus} autoComplete="new-password"
          placeholder="At least 6 characters"
          className="w-full bg-transparent py-2.5 px-3 text-sm outline-none" />
      </div>
    </label>
  );
}
